<template>
  <div class="transactions-ctn pb-6 mr-4">
    <h4 class="text-capitalize font-weight-bold title">{{$t('amazon_transparency_tbya_transactions')}}</h4>

    <!-- ATP disabled -->
    <div v-if="isATPEnabled">
      <!-- select filters and search field   -->
      <div class="mt-10 d-flex align-center flex-wrap">
        <div class="d-flex align-center">
          <v-icon color="black" size="24" class="align-self-center mr-6">mdi-filter-variant</v-icon>
          <v-select
            v-model="selectedSource"
            :items="sourceFilterOptions"
            :item-disabled="isItemDisabled"
            label="Source"
            dense
            solo
            height="40"
            class="custom-select elevation-0 mr-6"
          />
          <v-select
            v-model="selectedStatus"
            :items="statusFilterOptions"
            :item-disabled="isItemDisabled"
            label="Status"
            dense
            solo
            height="40"
            class="custom-select elevation-0 mr-6"
          />
        </div>
        <v-spacer></v-spacer>
        <v-text-field
          outlined
          dense
          v-model="searchQuery"
          class="transactions-search-field mt-4 mt-sm-0"
          :placeholder="$t('amazon_transparency_search_by')"
          @input="handleSearchInput"
          single-line
          persistent-hint
        >
          <template v-slot:append>
            <v-icon
              color="black"
              @click="handleSearchIconClick"
              class="cursor-pointer"
            >
              {{ searchQuery ? 'mdi-close' : 'mdi-magnify' }}
            </v-icon>
          </template>
        </v-text-field>
      </div>

      <!--  transactions table   -->
      <div class="mt-6">
        <v-data-table
          :headers="headers"
          :items="transactions"
          :loading="isLoadingTransactions"
          :options.sync="options"
          :server-items-length="totalTransactions"
          :items-per-page="10"
          :footer-props="{
              'items-per-page-text': '',
              'disable-items-per-page': true
      }"
          class="tbya-transactions-table"
          item-value="id"
          :loading-text="$t('amazon_transparency_loading_transactions')"
          hide-default-footer
        >

          <!-- product name header  -->
          <template v-slot:header.product_name="{ header }">
            <span class="text-no-wrap">{{ header.text }}</span>
          </template>

          <!-- status -->
          <template v-slot:item.status="{ item }">
            <span class="text-capitalize" :class="utils.getStatusStyle(item.status.toLowerCase())">
              {{ utils.transformStatusText(item.status) }}
            </span>
          </template>

          <template v-slot:item.sku="{ item }">
            <span class="wrap-cell-text">{{ item.sku }}</span>
          </template>

          <!-- details -->
          <template v-slot:item.details="{ item }">
            <v-tooltip left content-class="custom-tooltip">
              <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                <v-menu offset-y>
                  <template #activator="{ on: menuOn, attrs: menuAttrs }">
                    <v-btn
                      icon
                      v-bind="{ ...tooltipAttrs, ...menuAttrs }"
                      v-on="{ ...tooltipOn, ...menuOn }"
                      @click="viewDetails(item)"
                    >
                      <v-icon color="primary" size="24">mdi-open-in-new</v-icon>
                    </v-btn>
                  </template>
                </v-menu>
              </template>
              <span class="tooltip-text-color">{{ $t('amazon_transparency_view_details') }}</span>
            </v-tooltip>
          </template>

          <!-- table footer  -->
          <template v-slot:footer>
            <div class="d-flex justify-end mt-5 mb-4">
              <v-pagination
                class="flex-grow-0"
                v-model="options.page"
                :length="totalPages"
                :total-visible="7"
                @click.stop
              />
            </div>
          </template>
        </v-data-table>
      </div>
    </div>

    <!-- ATP disabled -->
    <div v-if="!isATPEnabled">
      <div class="mt-10">
        <p class="content-text">{{$t('amazon_transparency_auto_register')}}</p>
        <p class="content-text mt-6">
          <span class="font-weight-bold">{{$t('amazon_transparency_already_register')}}</span>
          {{$t('amazon_transparency_use_module')}}
        </p>
        <p class="mt-6 content-text">
          {{$t('amazon_transparency_please_contact')}}
          <a href="mailto:support@scantrust.com" class="text-decoration-underline">support@scantrust.com</a>
          {{$t('amazon_transparency_enable_feature')}}
        </p>
      </div>
    </div>

    <TransactionDetailDialog :display-detail-dialog="displayDetailDialog" @update:displayDetailDialog="closeDialog" :transaction="selectedItem"/>
  </div>
</template>

<script>
import TransactionDetailDialog from '@/components/amazon-transparency/dialog/TransactionDetailDialog.vue'
import Utils from '@/utils/utils'
import { mapGetters } from 'vuex'

export default {
  name: 'Transactions',
  components: {
    TransactionDetailDialog
  },
  data () {
    return {
      selectedSource: this.$t('amazon_transparency_source'),
      selectedStatus: this.$t('amazon_transparency_status_header'),
      sourceFilterOptions: [
        { text: this.$t('amazon_transparency_source'), value: null, disabled: true },
        { text: this.$t('amazon_transparency_all'), value: 'all' },
        { text: this.$t('amazon_transparency_sweep'), value: 'sweep' },
        // { text: this.$t('amazon_transparency_workorder_creation'), value: 'workorder' },
        // { text: this.$t('amazon_transparency_code_ingestion'), value: 'code_ingestion' },
        // { text: this.$t('amazon_transparency_code_activation'), value: 'scm_transaction' },
        { text: this.$t('amazon_transparency_verification'), value: 'verification' }
      ],
      statusFilterOptions: [
        { text: this.$t('amazon_transparency_status_header'), value: null, disabled: true },
        { text: this.$t('amazon_transparency_all'), value: 'all' },
        { text: this.$t('amazon_transparency_pending'), value: 'pending' },
        { text: this.$t('amazon_transparency_failed'), value: 'failed' },
        { text: this.$t('amazon_transparency_completed'), value: 'success' }
      ],
      displayDetailDialog: false,
      searchQuery: '',
      headers: [
        { text: this.$t('amazon_transparency_product_name'), align: 'left', sortable: false, value: 'product_name', width: '15%' },
        { text: this.$t('amazon_transparency_sku'), align: 'center', sortable: false, value: 'sku', width: '17%' },
        { text: this.$t('amazon_transparency_source'), value: 'source', sortable: false, align: 'center', width: '10%' },
        { text: this.$t('amazon_transparency_connected_codes'), value: 'code_count', sortable: false, align: 'center', width: '13%', class: 'text-no-wrap' },
        { text: this.$t('amazon_transparency_status_header'), value: 'status', sortable: false, align: 'center', width: '12%' },
        { text: this.$t('amazon_transparency_date'), value: 'created_at', sortable: false, align: 'center', width: '18%', cellClass: 'text-no-wrap' },
        { text: this.$t('amazon_transparency_details'), value: 'details', sortable: false, align: 'right', width: '5%' }
      ],
      defaultParams: { ordering: '-created_at', limit: 10 },
      options: {
        page: 1,
        itemsPerPage: 10
      },
      debounceSearch: null,
      selectedItem: null
    }
  },
  computed: {
    ...mapGetters(['transactions', 'isLoadingTransactions', 'totalTransactions', 'getATPConfig']),
    isATPEnabled () {
      return this.getATPConfig && this.getATPConfig.is_enabled
    },
    utils () {
      return Utils
    },
    totalPages () {
      return Math.ceil(this.totalTransactions / this.options.itemsPerPage)
    }
  },
  methods: {
    viewDetails (item) {
      this.displayDetailDialog = true
      this.selectedItem = item
    },
    closeDialog () {
      this.displayDetailDialog = false
    },
    getUrlParams (extraParams) {
      const merged = { ...this.defaultParams, ...extraParams }
      const params = new URLSearchParams()

      for (const key in merged) {
        if (merged[key] != null && merged[key] !== '') {
          params.append(key, merged[key])
        }
      }

      return params
    },
    isItemDisabled (item) {
      return item.disabled
    },
    async dispatchLoadingState (loadingState) {
      await this.$store.dispatch('setIsLoadingTransactions', loadingState)
    },
    async displaySnackbar (message) {
      await this.$store.dispatch('showSnackBar', { message: message })
    },
    handleSearchInput (input) {
      const inputVal = input.trim()

      inputVal && this.debounceSearch()
    },
    async loadTransactions () {
      try {
        if (this.isLoadingTransactions) return
        const { page = 1, itemsPerPage = 10 } = this.options
        const extraParams = {
          offset: (page - 1) * itemsPerPage,
          limit: itemsPerPage
        }

        if (this.selectedStatus.toLowerCase() !== 'all' && this.selectedStatus.toLowerCase() !== 'status') {
          extraParams.status = this.selectedStatus
        }

        if (this.selectedSource.toLowerCase() !== 'all' && this.selectedSource.toLowerCase() !== 'source') {
          extraParams.source = this.selectedSource
        }

        if (this.searchQuery !== '') {
          extraParams.search = this.searchQuery.trim()
        }

        const payload = this.getUrlParams(extraParams)
        await this.dispatchLoadingState(true)
        await this.$store.dispatch('loadTransactions', payload)
        await this.dispatchLoadingState(false)
      } catch (error) {
        await this.dispatchLoadingState(false)
        if (error?.response) {
          await this.displaySnackbar(this.$t('amazon_transparency_error_occurred'))
        } else if (error?.request) {
          await this.displaySnackbar(this.$t('amazon_transparency_no_response'))
        } else {
          await this.displaySnackbar(this.$t('amazon_transparency_error_occurred'))
        }
        await this.$store.dispatch('setTransactions', [])
      }
    },
    resetAndReloadTransactions () {
      this.options.page = 1
      this.$store.dispatch('setTransactions', [])
      this.loadTransactions()
    },
    async clearSearch () {
      this.searchQuery = ''
      await this.loadTransactions()
    },
    handleSearchIconClick () {
      if (this.searchQuery) {
        this.clearSearch()
      }
    }
  },
  watch: {
    searchQuery (newVal) {
      if (newVal === '' && this.transactions.length === 0) {
        this.loadTransactions()
      }
    },
    options: {
      handler () {
        this.loadTransactions()
      },
      deep: true
    },
    selectedStatus () {
      this.resetAndReloadTransactions()
    },
    selectedSource () {
      this.resetAndReloadTransactions()
    }
  },
  created () {
    this.debounceSearch = Utils.debounce(() => {
      this.$store.dispatch('setTransactions', [])
      this.loadTransactions()
    }, 400, false)
  }
}
</script>

<style lang="sass">

  @mixin text-styles($color)
    color: $color
    font-size: 14px
    font-style: normal
    font-weight: 400
    line-height: 20px
    letter-spacing: 0.25px

  .transactions-ctn
    padding-top: 20px
    max-height: 100vh
    overflow-y: auto
    -ms-overflow-style: none
    scrollbar-width: none

    &::-webkit-scrollbar
      display: none

    .title
      font-size: 24px
      font-style: normal
      font-weight: 700
      line-height: normal

    .content-text
      font-size: 18px
      font-style: normal
      font-weight: 300
      line-height: 26px

  .custom-select
    max-width: 137px !important
    width: 137px !important
    height: 45px !important
    max-height: 45px !important

    .v-input__append-inner
      width: 20px

    .v-input__control
      height: 45px !important
      max-height: 45px !important
      width: 100% !important

    .v-input__slot
      border: 1px solid rgba(0, 0, 0, 0.1)
      height: 100% !important
      box-shadow: 0 1px 1px rgba(0, 0, 0, 0.1) !important

    input
      height: 100% !important
      width: 100% !important

  .transactions-search-field
    max-width: 507px !important
    margin-bottom: 4px !important

    .v-input__control
      height: 40px !important
      border: 1px solid rgba(0, 0, 0, 0.10) !important
      border-radius: 5px !important
      background: #fff

    .v-input__slot
      height: 40px !important

    input
      height: 40px !important
      line-height: 40px !important

  .text-success
    @include text-styles(#3FC135)

  .text-info
    @include text-styles(#00AEEF)

  .text-normal
    @include text-styles(#000)

  .text-pending
    @include text-styles(#999)

  .text-danger
    @include text-styles(#FA4C5B)

  .tbya-transactions-table
    .v-data-table-header
      background-color: #f5f5f5 !important

    .v-data-table-header th
      font-weight: 700 !important
      color: rgba(0, 0, 0, 0.87) !important
      font-size: 12px !important
      line-height: 20px !important
      letter-spacing: 0.4px !important

    .v-data-footer__select
      visibility: hidden !important
      pointer-events: none !important
</style>
